<template>
  <PbHeader class="pb-profile-header">
    <template
      v-if="profile && translationsLoaded"
      #subtitle
    >
      <div class="pb-profile-header-name-responsive ellipsis">
        {{ profile?.name }}
        <UiIcon
          v-tooltip="{ text: reportShortText }"
          :icon="reportIcon"
          :color="reportColor"
        />
      </div>
    </template>

    <div
      v-if="profile && translationsLoaded"
      class="pb-profile-header-infos"
    >
      <div class="pb-profile-header-name">
        {{ name }}
      </div>
      <div class="pb-profile-header-meta">
        <span class="txt-muted txt-small">
          {{ lastEdit }}
        </span>
      </div>
    </div>

    <template
      v-if="profile && translationsLoaded"
      #responsive
    >
      <div
        v-if="uid"
        class="pb-profile-header-actions"
      >
        <BaseButton
          :to="profileStore.reportLink"
          :aria-label="translate('pb2.profileHeaderView.openReportTooltip')"
          :tooltip="translate('pb2.profileHeaderView.openReportTooltip')"
          class="pb-profile-header-report-label"
          :class="reportColor"
        >
          <UiBadge
            :color="reportColor"
            interactive
          >
            <UiIcon
              :icon="reportIcon"
              size="small"
            />
            {{ reportShortText }}
          </UiBadge>
        </BaseButton>
        <InlineSelectorContainer class="pb-profile-header-view-selector">
          <InlineSelectorItem
            :to="{ name: routeNames.Properties, params: { profileUid: uid } }"
            :as="PbLink"
          >
            {{ translate('pb2.profileHeaderView.propertiesSelectorItem') }}
          </InlineSelectorItem>
          <InlineSelectorItem
            :to="{ name: routeNames.Content, params: { profileUid: uid } }"
            :as="PbLink"
          >
            {{ translate('pb2.profileHeaderView.contentSelectorItem') }}
          </InlineSelectorItem>
        </InlineSelectorContainer>

        <DownloadProfileButton />
      </div>
    </template>
  </PbHeader>
  <ReportDialog
    v-bind="reportDialogControl"
  />
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Time } from '@nicolaudie-group/ls-utilities'
import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import useHotkeys, { KEYS } from '@nicolaudie-group/ng-ui-kit/composables/useHotkeys'
import useCloseOthers from '@nicolaudie-group/ng-ui-kit/composables/useCloseOthers'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import UiBadge from '@nicolaudie-group/ng-ui-kit/components/UiBadge/UiBadge.vue'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import InlineSelectorContainer from '@nicolaudie-group/ng-ui-kit/components/InlineSelector/InlineSelectorContainer.vue'
import InlineSelectorItem from '@nicolaudie-group/ng-ui-kit/components/InlineSelector/InlineSelectorItem.vue'

import useProfileStore from '@/stores/profile.store'
import routeNames from '@/router/names'

import useInterval from '@/composables/useInterval'
import useDialog from '@/composables/useDialog'
import { useProfile, useReport } from '@/composables/useProfile'
import useReportBadge from '@/composables/useReportBadge'
import useTranslate from '@/composables/useTranslate'

import PbLink from '@/components/PbLink.vue'
import ReportDialog from '@/components/ReportDialog/ReportDialog.vue'
import DownloadProfileButton from '@/components/DownloadProfileButton.vue'
import PbHeader from '@/components/PbHeader/PbHeader.vue'

const { translate, translationsLoaded } = useTranslate()

const profileStore = useProfileStore()
const { interval } = useInterval()

const profile = useProfile()
const report = useReport()

const name = computed(() => {
  if (profile.value) {
    return profile.value.name
  }
  return ''
})

const uid = computed(() => {
  if (profile.value) {
    return profile.value.uid
  }
  return null
})

const {
  level: reportColor,
  icon: reportIcon,
  shortText: reportShortText,
} = useReportBadge(report)

const lastEdit = ref('')
function updateLastEdit() {
  if (profile.value && translationsLoaded.value) {
    const date = Time.parseDate(profile.value.updatedDate, 'raw')
    lastEdit.value = `${translate('pb2.profileHeaderView.lastEdit')} ${date.fromNow()}`
  }
}

watch(profile, updateLastEdit, { immediate: true })
interval(updateLastEdit, 1000)

const {
  control: reportDialogControl,
  toggle,
  close,
  state,
} = useDialog({ query: 'report' })
useCloseOthers(close, state)

const { active: f1Pressed } = useHotkeys(KEYS.F1, {
  shift: false,
  alt: false,
  meta: false,
  ctrl: false,
  exact: true,
})

watch(f1Pressed, (newValue) => {
  if (newValue) {
    toggle()
  }
})
</script>

<style lang="scss" src="./ProfileHeader.scss" />
